import { createBrowserRouter, Navigate, useRouteError, useParams } from 'react-router-dom';
import { ProtectedRoute } from '@/features/auth';
import { MainLayout } from '@/components/layout';

// Lazy load pages for code splitting
import { lazy, Suspense } from 'react';

// Retry dynamic imports by reloading the page on chunk load failure
function lazyWithRetry(importFn: () => Promise<{ default: React.ComponentType }>) {
  return lazy(() =>
    importFn().catch(() => {
      window.location.reload();
      return new Promise(() => {});
    })
  );
}

// Public pages
const LandingPage = lazyWithRetry(() => import('@/pages/landing.page'));
const LoginPage = lazyWithRetry(() => import('@/pages/login.page'));
const DemoPage = lazyWithRetry(() => import('@/pages/demo.page'));

// Protected app pages
const DashboardPage = lazyWithRetry(() => import('@/pages/dashboard.page'));
const JobDetailPage = lazyWithRetry(() => import('@/pages/job-detail.page'));
const InsuredDetailPage = lazyWithRetry(() => import('@/pages/insured-detail.page'));
const RiskRequirementEditorPage = lazyWithRetry(() => import('@/pages/risk-requirement-editor.page'));
const SearchPage = lazyWithRetry(() => import('@/pages/search.page'));
const InsuredsPage = lazyWithRetry(() => import('@/pages/insureds.page'));
const RiskRequirementsPage = lazyWithRetry(() => import('@/pages/risk-requirements.page'));
const JobsPage = lazyWithRetry(() => import('@/pages/jobs.page'));
const ReportsPage = lazyWithRetry(() => import('@/pages/reports.page'));
const SettingsPage = lazyWithRetry(() => import('@/pages/settings.page'));
const LiveScanningPage = lazyWithRetry(() => import('@/pages/live-scanning.page'));
const UploadDocumentPage = lazyWithRetry(() => import('@/pages/upload-document.page'));
const InsuredPortalPage = lazyWithRetry(() => import('@/pages/insured-portal.page'));
const InsuredPortalScanPage = lazyWithRetry(() => import('@/pages/insured-portal-scan.page'));
const ReportDetailPage = lazyWithRetry(() => import('@/pages/report-detail.page'));
const DepartmentsPage = lazyWithRetry(() => import('@/pages/departments.page'));
const TeamPage = lazyWithRetry(() => import('@/pages/team.page'));
const TasksPage = lazyWithRetry(() => import('@/pages/tasks.page'));
const TaskDetailPage = lazyWithRetry(() => import('@/pages/task-detail.page'));
const NotificationsPage = lazyWithRetry(() => import('@/pages/notifications.page'));
const DirectoryPage = lazyWithRetry(() => import('@/pages/directory.page'));
const CommunicationTemplatesPage = lazyWithRetry(() => import('@/pages/communication-templates.page'));
const UploadFilesPage = lazyWithRetry(() => import('@/pages/upload-files.page'));

// Loading fallback
function PageLoader() {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
}

// Wrap lazy component with Suspense
function withSuspense(Component: React.LazyExoticComponent<React.ComponentType>) {
  return (
    <Suspense fallback={<PageLoader />}>
      <Component />
    </Suspense>
  );
}

// Layout wrapper for protected routes
function ProtectedLayout({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute>
      <MainLayout>{children}</MainLayout>
    </ProtectedRoute>
  );
}

// Legacy redirect component that properly interpolates route params
function ProjectRedirect() {
  const { jobId } = useParams<{ jobId: string }>();
  return <Navigate to={`/app/job/${jobId}`} replace />;
}

// Root error fallback for React Router — catches errors that bypass lazyWithRetry
function RouterErrorFallback() {
  const error = useRouteError();
  const message = error instanceof Error ? error.message : '';
  const isChunkError =
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('error loading dynamically imported module') ||
    message.includes('Loading chunk') ||
    message.includes('Loading CSS chunk') ||
    message.includes('Unable to preload CSS for');

  if (isChunkError) {
    window.location.reload();
    return null;
  }

  return (
    <div className="flex h-screen flex-col items-center justify-center gap-4 p-8">
      <h2 className="text-xl font-semibold text-red-600">Something went wrong</h2>
      <p className="max-w-md text-center text-gray-600">
        An unexpected error occurred. Please try refreshing the page.
      </p>
      {error instanceof Error && (
        <pre className="mt-2 max-w-2xl overflow-auto rounded bg-gray-100 p-4 text-xs text-gray-700">
          {error.message}
          {'\n\n'}
          {error.stack}
        </pre>
      )}
      <button
        onClick={() => window.location.reload()}
        className="mt-2 rounded-lg bg-gray-200 px-6 py-2.5 hover:bg-gray-300"
      >
        Refresh Page
      </button>
    </div>
  );
}

export const router = createBrowserRouter([
  {
    errorElement: <RouterErrorFallback />,
    children: [
  // ============================================
  // PUBLIC ROUTES
  // ============================================

  // Landing page (marketing)
  {
    path: '/',
    element: withSuspense(LandingPage),
  },

  // Login page
  {
    path: '/login',
    element: withSuspense(LoginPage),
  },

  // Demo mode - shows app UI with mock data (no auth required)
  {
    path: '/demo',
    element: withSuspense(DemoPage),
  },
  {
    path: '/demo/project/:jobId',
    element: withSuspense(DemoPage),
  },

  // Insured Portal (public upload page)
  {
    path: '/insured/:token',
    element: withSuspense(InsuredPortalPage),
  },
  {
    path: '/insured/:token/scan/:documentId',
    element: withSuspense(InsuredPortalScanPage),
  },

  // ============================================
  // PROTECTED ROUTES (requires authentication)
  // ============================================

  // Main dashboard
  {
    path: '/app',
    element: <ProtectedLayout>{withSuspense(DashboardPage)}</ProtectedLayout>,
  },

  // Dashboard redirect
  {
    path: '/app/dashboard',
    element: <Navigate to="/app" replace />,
  },

  // Search
  {
    path: '/app/search',
    element: <ProtectedLayout>{withSuspense(SearchPage)}</ProtectedLayout>,
  },

  // Jobs list (backed by Projects API, labeled as "Jobs" in UI)
  {
    path: '/app/jobs',
    element: <ProtectedLayout>{withSuspense(JobsPage)}</ProtectedLayout>,
  },

  // Job detail (backed by Projects API, labeled as "Job" in UI)
  {
    path: '/app/job/:jobId',
    element: <ProtectedLayout>{withSuspense(JobDetailPage)}</ProtectedLayout>,
  },

  // Insureds list
  {
    path: '/app/insureds',
    element: <ProtectedLayout>{withSuspense(InsuredsPage)}</ProtectedLayout>,
  },

  // Insured detail
  {
    path: '/app/insured/:insuredId',
    element: <ProtectedLayout>{withSuspense(InsuredDetailPage)}</ProtectedLayout>,
  },

  // Risk requirements templates
  {
    path: '/app/risk-requirements',
    element: <ProtectedLayout>{withSuspense(RiskRequirementsPage)}</ProtectedLayout>,
  },

  // Risk requirement create (must be before :riskRequirementId to avoid "new" as param)
  {
    path: '/app/risk-requirements/new',
    element: <ProtectedLayout>{withSuspense(RiskRequirementEditorPage)}</ProtectedLayout>,
  },

  // Risk requirement detail/edit
  {
    path: '/app/risk-requirement/:riskRequirementId',
    element: <ProtectedLayout>{withSuspense(RiskRequirementEditorPage)}</ProtectedLayout>,
  },

  // Reports hub
  {
    path: '/app/reports',
    element: <ProtectedLayout>{withSuspense(ReportsPage)}</ProtectedLayout>,
  },

  // Report detail
  {
    path: '/app/reports/:reportType',
    element: <ProtectedLayout>{withSuspense(ReportDetailPage)}</ProtectedLayout>,
  },

  // Settings
  {
    path: '/app/settings',
    element: <ProtectedLayout>{withSuspense(SettingsPage)}</ProtectedLayout>,
  },

  // Notifications
  {
    path: '/app/notifications',
    element: <ProtectedLayout>{withSuspense(NotificationsPage)}</ProtectedLayout>,
  },

  // Upload COI (triggers OCR processing)
  {
    path: '/app/documents/upload',
    element: <ProtectedLayout>{withSuspense(UploadDocumentPage)}</ProtectedLayout>,
  },

  // Upload Files (general documents, no OCR processing)
  {
    path: '/app/documents/upload-files',
    element: <ProtectedLayout>{withSuspense(UploadFilesPage)}</ProtectedLayout>,
  },

  // LIVE Document Scanning (side-by-side PDF + extracted data)
  {
    path: '/app/documents/scan/:documentId',
    element: <ProtectedLayout>{withSuspense(LiveScanningPage)}</ProtectedLayout>,
  },

  // Departments
  {
    path: '/app/departments',
    element: <ProtectedLayout>{withSuspense(DepartmentsPage)}</ProtectedLayout>,
  },

  // Team (User Profiles)
  {
    path: '/app/team',
    element: <ProtectedLayout>{withSuspense(TeamPage)}</ProtectedLayout>,
  },

  // Tasks list
  {
    path: '/app/tasks',
    element: <ProtectedLayout>{withSuspense(TasksPage)}</ProtectedLayout>,
  },

  // Task detail
  {
    path: '/app/task/:taskId',
    element: <ProtectedLayout>{withSuspense(TaskDetailPage)}</ProtectedLayout>,
  },

  // Directory (Contacts)
  {
    path: '/app/directory',
    element: <ProtectedLayout>{withSuspense(DirectoryPage)}</ProtectedLayout>,
  },

  // Communication Templates
  {
    path: '/app/templates',
    element: <ProtectedLayout>{withSuspense(CommunicationTemplatesPage)}</ProtectedLayout>,
  },

  // ============================================
  // LEGACY REDIRECTS (old routes → new routes)
  // ============================================
  {
    path: '/app/projects',
    element: <Navigate to="/app/jobs" replace />,
  },
  {
    path: '/app/project/:jobId',
    element: <ProjectRedirect />,
  },
  {
    path: '/dashboard',
    element: <Navigate to="/app" replace />,
  },
  {
    path: '/search',
    element: <Navigate to="/app/search" replace />,
  },
  {
    path: '/project/:jobId',
    element: <ProjectRedirect />,
  },
  {
    path: '/jobs',
    element: <Navigate to="/app/jobs" replace />,
  },
  {
    path: '/insureds',
    element: <Navigate to="/app/insureds" replace />,
  },

  // ============================================
  // CATCH-ALL
  // ============================================
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
    ],
  },
]);
